import React from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { useFieldArray } from 'react-final-form-arrays';

import { closeOverviewModal } from 'modules/basket/actions';
import { useSelector } from 'hooks';
import { getCountry, getValueAddedTax } from 'modules/selfServices/selectors';
import { getSelectedSelfServiceId } from 'modules/auth/selectors';
import { useGetSelfServiceByIdQuery } from 'modules/selfServices/service';
import usePreAuthorizedAmounts from 'hooks/usePreAuthorizedAmounts';
import FormKeys from 'modules/form/types/FormKeys';
import { formatUndefined } from 'utils/formUtils';
import { computeTotalPrice, roundAmountByCountry } from 'utils/priceUtils';
import AdviceStatus from 'types/AdviceStatus';
import type { Extra } from 'types/ExtrasForm';
import { ProactiveWork } from 'types/RepairOrderForm';
import useRepairOrder from 'components/RepairOrder/useRepairOrder';

import BaseModal from '../../ui/Modal';
import Button, { ButtonType } from '../../ui/Button';

const BasketTaxDetailsModal = () => {
  const dispatch = useDispatch();

  const vat = useSelector(getValueAddedTax);
  const country = useSelector(getCountry);
  const selfServiceId = useSelector(getSelectedSelfServiceId);
  const { customerPreAuthorizedAmount } = usePreAuthorizedAmounts();
  const { data: selfService } = useGetSelfServiceByIdQuery({ id: selfServiceId });

  const { value: extras = [] } = useFieldArray<Extra>(`${FormKeys.EXTRAS}.extras`, {
    subscription: { value: true }, format: formatUndefined,
  }).fields;

  const { value: proactiveWorks = [] } = useFieldArray<ProactiveWork>(`${FormKeys.REPAIR_ORDER}.proactiveWorks`, {
    subscription: { value: true }, format: formatUndefined,
  }).fields;

  const acceptedExtras = React.useMemo(
    () => extras.filter((extra) => extra.status === AdviceStatus.ACCEPTED),
    [extras],
  );

  const acceptedProactiveWorks = React.useMemo(
    () => proactiveWorks.filter((proactiveWork) => proactiveWork.status === AdviceStatus.ACCEPTED),
    [proactiveWorks],
  );

  const { sortedRepairOrderItems } = useRepairOrder(selfService);

  const repairOrderItemsForCustomer = React.useMemo(
    () => sortedRepairOrderItems.filter((repairOrder) => !repairOrder.payerId),
    [sortedRepairOrderItems],
  );

  const totalExtras = React.useMemo(
    () => computeTotalPrice(acceptedExtras, vat),
    [acceptedExtras, vat],
  );

  const totalProactiveWorks = React.useMemo(
    () => computeTotalPrice(acceptedProactiveWorks, vat),
    [acceptedProactiveWorks, vat],
  );

  const totalRepairOrder = React.useMemo(
    () => customerPreAuthorizedAmount ?? computeTotalPrice(repairOrderItemsForCustomer, vat),
    [customerPreAuthorizedAmount, repairOrderItemsForCustomer, vat],
  );

  const totalInclTax = React.useMemo(
    () => roundAmountByCountry[country?.code](totalExtras + totalProactiveWorks + totalRepairOrder),
    [country?.code, totalExtras, totalProactiveWorks, totalRepairOrder],
  );

  const totalExclTax = React.useMemo(
    () => totalInclTax / (1 + vat / 100),
    [totalInclTax, vat],
  );

  const totalTax = React.useMemo(
    () => totalInclTax - totalExclTax,
    [totalInclTax, totalExclTax],
  );

  const handleCloseModal = () => {
    dispatch(closeOverviewModal());
  };

  if (typeof document === 'undefined') {
    return null;
  }

  return (
    <BaseModal
      open
      className="modal-large max-h-[95vh] flex flex-col md:max-w-96"
      title={<FormattedMessage id="basket.taxDetailsModal.title" defaultMessage="Basket overview" tagName="h2" />}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col">
          <div className="flex justify-between items-center">
            <span className="text-gray-400">
              <FormattedMessage
                id="basket.taxDetailsModal.content.totalExcludeTax"
                defaultMessage="Total (excl. tax)"
              />
            </span>
            <span className="text-gray-400 font-medium">
              {totalExclTax.toFixed(2)} {country?.currency}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-400">
              <FormattedMessage
                id="basket.taxDetailsModal.content.totalTax"
                defaultMessage="Total tax"
              />
            </span>
            <span className="text-gray-400 font-medium">
              {totalTax.toFixed(2)} {country?.currency}
            </span>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-default font-semibold">
            <FormattedMessage
              id="basket.taxDetailsModal.content.totalIncludeTax"
              defaultMessage="Total (incl. tax)"
            />
          </span>
          <span className="text-default font-semibold">
            {totalInclTax.toFixed(2)} {country?.currency}
          </span>
        </div>

        <Button
          onClick={handleCloseModal}
          className="mx-auto md:max-w-96 w-full"
          type={ButtonType.SECONDARY}>
          <FormattedMessage id="close" defaultMessage="Close" />
        </Button>
      </div>
    </BaseModal>
  );
};

export default BasketTaxDetailsModal;
